import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import {
  type ChatMessage,
  type ConversationWithDetails,
  type CreateConversationPayload,
  type SendMessagePayload,
  deescalateConversation,
  escalateConversation,
  findOrCreateConversation,
  getConversation,
  getConversationByOrderId,
  getConversationMessages,
  getConversations,
  markMessagesAsRead,
  sendMessage,
} from "../services/chat";

export type { ChatMessage, ConversationWithDetails };

const CONVERSATIONS_PAGE_SIZE = 10;

export const useChatConversations = () => {
  return useInfiniteQuery<
    ConversationWithDetails[],
    Error,
    InfiniteData<ConversationWithDetails[]>,
    string[],
    number
  >({
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < CONVERSATIONS_PAGE_SIZE) return undefined;
      return allPages.flat().length;
    },
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      getConversations(CONVERSATIONS_PAGE_SIZE, pageParam),
    queryKey: ["chat", "conversations"],
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const flattenConversationPages = (
  data: InfiniteData<ConversationWithDetails[]> | undefined,
): ConversationWithDetails[] => {
  if (!data) return [];
  return data.pages.flat();
};

export const useConversation = (conversationId: string | null) => {
  return useQuery({
    enabled: !!conversationId,
    queryFn: () => getConversation(conversationId!),
    queryKey: ["chat", "conversation", conversationId],
    refetchOnWindowFocus: false,
  });
};

export const useConversationByOrderId = (orderId: string | null) => {
  return useQuery({
    enabled: !!orderId,
    queryFn: () => getConversationByOrderId(orderId!),
    queryKey: ["chat", "conversation", "by-order", orderId],
    refetchOnWindowFocus: false,
    retry: false,
  });
};

const MESSAGES_PAGE_SIZE = 20;

export const useConversationMessages = (conversationId: string | null) => {
  return useInfiniteQuery<
    ChatMessage[],
    Error,
    InfiniteData<ChatMessage[]>,
    (string | null)[],
    number
  >({
    enabled: !!conversationId,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < MESSAGES_PAGE_SIZE) return undefined;
      return allPages.flat().length;
    },
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) =>
      getConversationMessages(conversationId!, MESSAGES_PAGE_SIZE, pageParam),
    queryKey: ["chat", "messages", conversationId],
    refetchOnMount: "always",
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const flattenMessagePages = (
  data: InfiniteData<ChatMessage[]> | undefined,
): ChatMessage[] => {
  if (!data) return [];
  return [...data.pages].reverse().flat();
};

export const useFindOrCreateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateConversationPayload) =>
      findOrCreateConversation(payload),
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error?.response?.data?.message || "Failed to create conversation",
      );
    },
    onSuccess: (newConversation) => {
      queryClient.setQueryData(
        ["chat", "conversations"],
        (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
          if (!old) return { pageParams: [0], pages: [[newConversation]] };
          const firstPage = old.pages[0];
          const exists = firstPage.find(
            (c: ConversationWithDetails) =>
              c.conversation.id === newConversation.conversation.id,
          );
          if (exists) return old;
          return {
            ...old,
            pages: [[newConversation, ...firstPage], ...old.pages.slice(1)],
          };
        },
      );
    },
  });
};

interface SendMessageWithUserPayload extends SendMessagePayload {
  currentUserId: string;
}

interface SendMessageContext {
  conversationId: string;
  tempId: string;
}

export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ChatMessage,
    { response?: { data?: { message?: string } } },
    SendMessageWithUserPayload,
    SendMessageContext
  >({
    mutationFn: (payload: SendMessageWithUserPayload) =>
      sendMessage({
        attachments: payload.attachments,
        content: payload.content,
        conversationId: payload.conversationId,
      }),
    onError: (error, _, context) => {
      toast.error(error?.response?.data?.message || "Failed to send message");

      if (context?.tempId && context?.conversationId) {
        queryClient.setQueryData(
          ["chat", "messages", context.conversationId],
          (old: InfiniteData<ChatMessage[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((msg) =>
                  msg.id === context.tempId
                    ? { ...msg, sendingStatus: "failed" as const }
                    : msg,
                ),
              ),
            };
          },
        );
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: ["chat", "messages", payload.conversationId],
      });

      const tempId = `temp-${Date.now()}`;
      const now = new Date().toISOString();
      const optimisticMessage: ChatMessage = {
        attachments: payload.attachments,
        content: payload.content,
        conversationId: payload.conversationId,
        createdAt: now,
        id: tempId,
        isRead: false,
        messageType: "TEXT",
        readAt: null,
        senderId: payload.currentUserId,
        senderIsDriver: false,
        sendingStatus: "sending",
        updatedAt: now,
      };

      queryClient.setQueryData(
        ["chat", "messages", payload.conversationId],
        (old: InfiniteData<ChatMessage[]> | undefined) => {
          if (!old) return { pageParams: [0], pages: [[optimisticMessage]] };
          const lastPageIndex = old.pages.length - 1;
          return {
            ...old,
            pages: [
              ...old.pages.slice(0, lastPageIndex),
              [...old.pages[lastPageIndex], optimisticMessage],
            ],
          };
        },
      );

      queryClient.setQueryData(
        ["chat", "conversations"],
        (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
          if (!old) return old;

          const updatedPages = old.pages.map((page) =>
            page.map((conv: ConversationWithDetails) => {
              if (conv.conversation.id === payload.conversationId) {
                return {
                  ...conv,
                  conversation: {
                    ...conv.conversation,
                    lastMessageAt: now,
                  },
                  lastMessage: optimisticMessage,
                };
              }
              return conv;
            }),
          );

          const allConversations = updatedPages.flat();
          allConversations.sort(
            (a: ConversationWithDetails, b: ConversationWithDetails) =>
              new Date(b.conversation.lastMessageAt).getTime() -
              new Date(a.conversation.lastMessageAt).getTime(),
          );

          const newPages: ConversationWithDetails[][] = [];
          for (
            let i = 0;
            i < allConversations.length;
            i += CONVERSATIONS_PAGE_SIZE
          ) {
            newPages.push(
              allConversations.slice(i, i + CONVERSATIONS_PAGE_SIZE),
            );
          }
          if (newPages.length === 0) newPages.push([]);

          return { ...old, pages: newPages };
        },
      );

      return { conversationId: payload.conversationId, tempId };
    },
    onSuccess: (message, _, context) => {
      if (context?.tempId) {
        queryClient.setQueryData(
          ["chat", "messages", message.conversationId],
          (old: InfiniteData<ChatMessage[]> | undefined) => {
            if (!old) return { pageParams: [0], pages: [[message]] };
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((msg) =>
                  msg.id === context.tempId
                    ? { ...message, sendingStatus: "sent" as const }
                    : msg,
                ),
              ),
            };
          },
        );

        queryClient.setQueryData(
          ["chat", "conversations"],
          (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((conv: ConversationWithDetails) => {
                  if (conv.conversation.id === message.conversationId) {
                    return {
                      ...conv,
                      lastMessage: { ...message, sendingStatus: "sent" },
                    };
                  }
                  return conv;
                }),
              ),
            };
          },
        );
      }
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
    }: {
      conversationId: string;
      currentUserId: string;
    }) => markMessagesAsRead(conversationId),
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error?.response?.data?.message || "Failed to mark messages as read",
      );
    },
    onSuccess: (_, { conversationId, currentUserId }) => {
      const conversationsData = queryClient.getQueryData<
        InfiniteData<ConversationWithDetails[]>
      >(["chat", "conversations"]);
      const conversation = conversationsData?.pages
        .flat()
        .find((c) => c.conversation.id === conversationId);
      const isOrderScoped = !!conversation?.conversation.orderId;

      queryClient.setQueryData(
        ["chat", "conversations"],
        (old: InfiniteData<ConversationWithDetails[]> | undefined) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) =>
              page.map((conv: ConversationWithDetails) => {
                if (conv.conversation.id === conversationId) {
                  const shouldMarkLastMessageRead = conv.lastMessage
                    ? isOrderScoped
                      ? conv.lastMessage.senderIsDriver
                      : conv.lastMessage.senderId !== currentUserId
                    : false;
                  return {
                    ...conv,
                    lastMessage:
                      conv.lastMessage && shouldMarkLastMessageRead
                        ? {
                            ...conv.lastMessage,
                            isRead: true,
                            readAt: new Date().toISOString(),
                          }
                        : conv.lastMessage,
                    unreadCount: 0,
                  };
                }
                return conv;
              }),
            ),
          };
        },
      );

      queryClient.setQueryData(
        ["chat", "messages", conversationId],
        (old: InfiniteData<ChatMessage[]> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) =>
              page.map((msg: ChatMessage) => {
                const shouldMarkRead = isOrderScoped
                  ? msg.senderIsDriver && !msg.isRead
                  : msg.senderId !== currentUserId && !msg.isRead;
                if (shouldMarkRead) {
                  return {
                    ...msg,
                    isRead: true,
                    readAt: new Date().toISOString(),
                  };
                }
                return msg;
              }),
            ),
          };
        },
      );
    },
  });
};

const invalidateConversation = (
  queryClient: ReturnType<typeof useQueryClient>,
  conversationId: string,
) => {
  queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
  queryClient.invalidateQueries({
    queryKey: ["chat", "conversation", conversationId],
  });
  queryClient.invalidateQueries({
    queryKey: ["chat", "messages", conversationId],
  });
};

export const useEscalateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      escalateConversation(conversationId),
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error?.response?.data?.message || "Failed to escalate conversation",
      );
    },
    onSuccess: (_, conversationId) => {
      invalidateConversation(queryClient, conversationId);
    },
  });
};

export const useDeescalateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      deescalateConversation(conversationId),
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(
        error?.response?.data?.message || "Failed to return to the assistant",
      );
    },
    onSuccess: (_, conversationId) => {
      invalidateConversation(queryClient, conversationId);
    },
  });
};
